import type { Locale } from "./i18n";
import type { LauncherFollower } from "./launcher-anchor";
import type {
	FabMode,
	FabPosition,
	OnboardingBullet,
	OnboardingConfig,
	OnboardingCopy,
} from "./types";

import { glyphOf } from "./glyphs";
import { setLanguage, t } from "./i18n";
import {
	computeBesideAnchor,
	computeLauncherAnchor,
	createLauncherFollower,
} from "./launcher-anchor";
import onboardingCss from "./onboarding.css?inline";
import { attachStyles } from "./styles";

interface OnboardingHandle extends LauncherFollower {
	readonly dispose: () => void;
	readonly hide: () => void;
	/** Replace the words without disturbing whether the card is showing. */
	readonly setCopy: (copy: OnboardingCopy) => void;
	readonly show: () => void;
	readonly showing: () => boolean;
}

interface OnboardingCallbacks {
	/** The visitor closed the card: the cross or the button. */
	readonly onClosed: () => void;
	/** The visitor took the card up on its offer – open the widget. */
	readonly onCta: () => void;
	/** The pointer entered or left the card itself. */
	readonly onHoverChange: (hovering: boolean) => void;
}

/**
 * The launcher's intro card. Closed shadow root like the FAB, and every string
 * is admin-authored, so all of it renders through `textContent` – customer
 * copy can never inject markup into the host page.
 *
 * The surface only draws what it is told to draw. Whether a visitor has earned
 * a peek, and whether the corner is free, are the caller's decisions.
 */
const mountOnboarding = (
	locale: Locale,
	position: FabPosition,
	mode: FabMode,
	config: OnboardingConfig,
	callbacks: OnboardingCallbacks,
): OnboardingHandle => {
	const host = document.createElement("div");
	host.id = "weloop-onboarding";
	host.dataset["position"] = position;
	host.dataset["mode"] = mode;
	host.toggleAttribute("data-hidden", true);
	const shadow = host.attachShadow({ mode: "closed" });

	attachStyles(shadow, onboardingCss);

	const card = document.createElement("div");
	card.className = "card";
	card.setAttribute("role", "dialog");
	card.setAttribute("aria-label", t(locale, "onboarding_label"));

	const head = document.createElement("div");
	head.className = "head";

	const spark = document.createElement("span");
	spark.className = "spark";
	spark.setAttribute("aria-hidden", "true");

	const headText = document.createElement("div");
	headText.className = "headText";

	const title = document.createElement("p");
	title.className = "title";

	const message = document.createElement("p");
	message.className = "message";

	const dismiss = document.createElement("button");
	dismiss.className = "dismiss";
	dismiss.type = "button";
	dismiss.setAttribute("aria-label", t(locale, "onboarding_dismiss"));
	// Inline multiplication sign as the close glyph – an SVG icon would cost
	// bundle bytes for no legibility gain at this size.
	dismiss.textContent = "×";

	headText.append(title, message);
	head.append(spark, headText, dismiss);

	const bullets = document.createElement("ul");
	bullets.className = "bullets";

	const cta = document.createElement("button");
	cta.className = "cta";
	cta.type = "button";

	card.append(head, bullets, cta);

	// The brand curtain: a block of color carrying the project's name,
	// which lifts to reveal the card the first time it shows. Decorative,
	// so it stays out of the accessibility tree. Like the rest of the card it
	// renders through `textContent` – a project name can never inject markup.
	// It belongs to the card that opens by itself: in `hover` mode the card
	// answers the pointer, and a second of branding before the words would
	// only get in the way. A config published before the curtain existed has
	// no project name, and opens plain too. Reads the config directly rather
	// than a shaped object – property names cost bytes on every host page.
	const hasCurtain =
		config.projectName !== null && config.trigger === "first-visit";
	if (hasCurtain) {
		const curtain = document.createElement("div");
		curtain.className = "curtain";
		curtain.setAttribute("aria-hidden", "true");
		// An inline style, not a custom property: fewer bytes on host pages.
		// An empty value is ignored, so the stylesheet's violet stays.
		curtain.style.background = config.brandColor ?? "";
		curtain.textContent = config.projectName;
		card.append(curtain);
	}

	shadow.append(card);
	document.body.append(host);

	// The card is a separate host element from the launcher, so reaching it
	// means the pointer leaves the launcher. Without tracking hover here too,
	// the card would vanish on its way to being clicked. Focus counts the same,
	// so a keyboard user can tab from the launcher onto the card's buttons.
	const handleEnter = (): void => {
		callbacks.onHoverChange(true);
	};
	const handleLeave = (): void => {
		callbacks.onHoverChange(false);
	};
	host.addEventListener("mouseenter", handleEnter);
	host.addEventListener("mouseleave", handleLeave);
	host.addEventListener("focusin", handleEnter);
	host.addEventListener("focusout", handleLeave);

	const applyCopy = (next: OnboardingCopy): void => {
		title.textContent = next.title;
		message.textContent = next.message ?? "";
		message.style.display = next.message === null ? "none" : "";
		if (next.lang === null) {
			card.removeAttribute("lang");
		} else {
			card.setAttribute("lang", next.lang);
		}

		bullets.replaceChildren();
		for (const bullet of next.bullets) {
			const item = document.createElement("li");
			item.className = "bullet";
			const mark = document.createElement("span");
			mark.className = "mark";
			mark.dataset["tone"] = bullet.tone;
			mark.setAttribute("aria-hidden", "true");
			mark.append(glyphOf(bullet.tone));
			item.append(mark, document.createTextNode(bullet.label));
			bullets.append(item);
		}
		bullets.style.display = next.bullets.length === 0 ? "none" : "";

		cta.textContent = next.ctaLabel ?? "";
		cta.style.display = next.ctaLabel === null ? "none" : "";
	};
	applyCopy(config.copy);

	// Closing hides the card through the controller: `onClosed` marks the
	// visitor and re-syncs, and a closed card is never visible.
	dismiss.addEventListener("click", callbacks.onClosed);
	cta.addEventListener("click", () => {
		callbacks.onClosed();
		callbacks.onCta();
	});

	// The curtain plays once per page, on the first time the card shows. Every
	// later showing resets the flag, so a hover gets the plain short fade.
	let isIntroDue = hasCurtain;

	// A moved launcher carries the card along: beside the vertical tab, the
	// way the stylesheet docks it, above or below the other launchers.
	const follower = createLauncherFollower(host, {
		anchorFor:
			mode === "vertical-tab" ? computeBesideAnchor : computeLauncherAnchor,
		fallbackSide: position,
	});

	return {
		dispose: () => {
			host.removeEventListener("mouseenter", handleEnter);
			host.removeEventListener("mouseleave", handleLeave);
			host.removeEventListener("focusin", handleEnter);
			host.removeEventListener("focusout", handleLeave);
			host.remove();
		},
		hide: () => {
			host.toggleAttribute("data-hidden", true);
		},
		setCopy: (next) => {
			applyCopy(next);
			// New words change the card's height.
			follower.place();
		},
		setLauncher: follower.setLauncher,
		setLauncherMoving: follower.setLauncherMoving,
		show: () => {
			// Only on the way in: the controller calls `show` on every sync,
			// and a repeat call must not cut a running curtain short.
			if (Object.hasOwn(host.dataset, "hidden")) {
				host.toggleAttribute("data-intro", isIntroDue);
				isIntroDue = false;
			}
			host.toggleAttribute("data-hidden", false);
			setLanguage(host, locale);
			// Placed in the same task it became visible, so the browser never
			// paints it at the old spot.
			follower.place();
		},
		showing: () => !Object.hasOwn(host.dataset, "hidden"),
	};
};

// --- Presentation policy ----------------------------------------------------
//
// The card is an introduction, not a notification, so the rules differ from the
// unread pill's: it waits for the page to settle before speaking up, and once
// the visitor closes it – the cross or the button – it never comes back, on a
// peek or on hover.

// The pause also gives the widget iframe time to report whether this user has
// already closed the card. Without it a repeat visitor sees the card appear and
// then vanish.
const PEEK_DELAY_MS = 1500;

// Grace period before a hover-shown card retracts. Long enough to cross the
// gap between the launcher and the card, short enough not to feel stuck.
const HOVER_GRACE_MS = 220;

// The intro is closed once per person. The durable record is server-side (the
// widget owns the session and reports it back), but that answer only arrives
// after the iframe authenticates. This local marker is the fast path: it stops
// a repeat visitor seeing the card flash before the verdict lands, and it is
// the only memory available for a visitor who never signs in.
const seenStorageKey = (projectId: string): string =>
	`weloop-onboarding-seen:${projectId}`;

const loadSeen = (projectId: string): boolean => {
	try {
		return window.localStorage.getItem(seenStorageKey(projectId)) !== null;
	} catch {
		// Locked-down host page (sandboxed iframe, blocked third-party storage).
		// The card degrades to one close per pageview instead of breaking the boot.
		return false;
	}
};

// `false` clears the marker – the signed-in user's record says not seen.
const saveSeen = (projectId: string, isSeen = true): void => {
	try {
		if (isSeen) {
			window.localStorage.setItem(
				seenStorageKey(projectId),
				new Date().toISOString(),
			);
		} else {
			window.localStorage.removeItem(seenStorageKey(projectId));
		}
	} catch {
		// Best effort – the server-side record still decides the next visit.
	}
};

export interface OnboardingController extends LauncherFollower {
	readonly dispose: () => void;
	/** Pointer entered or left the launcher. */
	readonly handleHover: (hovering: boolean) => void;
	/** The widget's per-user verdict: their language, and whether to stand down. */
	readonly handleVerdict: (
		seen: boolean,
		copy: OnboardingCopy | undefined,
	) => void;
	/** Re-evaluate whether the card should be on screen. */
	readonly sync: () => void;
}

interface ControllerArgs {
	readonly config: OnboardingConfig;
	/** False while a higher-priority surface owns the launcher corner. */
	readonly isSlotFree: () => boolean;
	readonly locale: Locale;
	readonly mode: FabMode;
	/** The visitor took the card up on its offer. */
	readonly onCta: () => void;
	/** Tell the widget to record that this user closed the card. */
	readonly onSeen: () => void;
	readonly position: FabPosition;
	readonly projectId: string;
}

/**
 * Owns the intro card's whole lifecycle: when it peeks, when it hides, and when
 * the visitor has closed it for good.
 *
 * Kept out of `bootstrap` because the policy is the interesting part – two
 * triggers, two sources of truth for "already closed" – and it
 * reads better next to the surface it drives than interleaved with the
 * launcher, the iframe, and the recorder.
 */
export const createOnboardingController = (
	args: ControllerArgs,
): OnboardingController => {
	let seen = loadSeen(args.projectId);
	// Whether the visitor closed the card on this page. Their close is on its
	// way to the server, so a verdict sent before it landed must not undo it.
	let closedHere = false;
	// A hover peek follows the pointer, so it is tracked apart from the
	// `first-visit` peek, which stays up by itself. The launcher and the card are
	// separate host elements, so hover is tracked per surface and the card
	// stays up while the pointer is over either of them.
	let launcherHovered = false;
	let cardHovered = false;
	let peeking = false;
	let peekTimer: ReturnType<typeof setTimeout> | undefined;
	let hideTimer: ReturnType<typeof setTimeout> | undefined;

	const endPeek = (): void => {
		if (peekTimer !== undefined) {
			clearTimeout(peekTimer);
			peekTimer = undefined;
		}
		peeking = false;
	};

	const markSeen = (): void => {
		if (seen) {
			return;
		}
		seen = true;
		closedHere = true;
		saveSeen(args.projectId);
		args.onSeen();
	};

	// Forward binding: the hover handlers are wired into the surface as it is
	// built, so they have to exist first. Set immediately after the mount, and
	// only ever called from a pointer event or a timer.
	let applyVisibility: ((visible: boolean) => void) | undefined = undefined;

	const sync = (): void => {
		applyVisibility?.(
			!seen && args.isSlotFree() && (launcherHovered || cardHovered || peeking),
		);
	};

	/**
	 * Hover changed on one of the two surfaces.
	 *
	 * Leaving is deliberately delayed. The card floats above the launcher with a
	 * gap between them, so a pointer travelling from the bubble to the button on
	 * the card is briefly over neither – hiding on that frame would make the
	 * button unreachable. Arriving cancels the pending hide.
	 */
	const setHovered = (
		surfaceHovered: "card" | "launcher",
		value: boolean,
	): void => {
		if (surfaceHovered === "card") {
			cardHovered = value;
		} else {
			launcherHovered = value;
		}
		if (hideTimer !== undefined) {
			clearTimeout(hideTimer);
			hideTimer = undefined;
		}
		if (launcherHovered || cardHovered) {
			sync();
			return;
		}
		hideTimer = setTimeout(() => {
			hideTimer = undefined;
			sync();
		}, HOVER_GRACE_MS);
	};

	const surface = mountOnboarding(
		args.locale,
		args.position,
		args.mode,
		args.config,
		{
			onClosed: () => {
				endPeek();
				markSeen();
				sync();
			},
			onCta: args.onCta,
			onHoverChange: (hovering) => {
				setHovered("card", hovering);
			},
		},
	);

	applyVisibility = (visible) => {
		if (visible) {
			surface.show();
		} else {
			surface.hide();
		}
	};

	// `first-visit` opens the card by itself on every page until the visitor
	// closes it. `hover` mode skips the peek – the card is there, but only when
	// asked for.
	const armPeek = (): void => {
		if (args.config.trigger !== "first-visit" || seen) {
			return;
		}
		peekTimer = setTimeout(() => {
			peekTimer = undefined;
			// The iframe may have reported "already closed" while we waited.
			if (seen) {
				return;
			}
			peeking = true;
			sync();
		}, PEEK_DELAY_MS);
	};
	armPeek();

	return {
		dispose: () => {
			endPeek();
			if (hideTimer !== undefined) {
				clearTimeout(hideTimer);
				hideTimer = undefined;
			}
			surface.dispose();
		},
		handleHover: (hovering) => {
			setHovered("launcher", hovering);
		},
		handleVerdict: (alreadySeen, copy) => {
			if (alreadySeen) {
				// This user closed the card on an earlier visit or another device:
				// stand down before the peek timer fires, and for hover too.
				endPeek();
				seen = true;
				saveSeen(args.projectId);
				sync();
				return;
			}
			// The marker is per browser and project, not per person: someone
			// else closed the card here, or this user's record was reset. The
			// server knows who is signed in, so its "not seen" wins.
			if (seen && !closedHere) {
				seen = false;
				saveSeen(args.projectId, false);
				armPeek();
			}
			if (copy !== undefined) {
				surface.setCopy(copy);
			}
		},
		setLauncher: surface.setLauncher,
		setLauncherMoving: surface.setLauncherMoving,
		sync,
	};
};

export type { OnboardingBullet };
